<script lang="ts">
    import * as t from '../../translations/News.ts';
    import Tabs from './Tabs.svelte';
    import type { BlogPost } from '../../api/blogs.ts';
    import type { Newsletter } from '../../api/news.ts';
    import { onMount } from 'svelte';
    import BlogCard from '../../lib/News/BlogCard.svelte';
    import NewsCard from '../../lib/News/NewsCard.svelte';
    import Pagination from '../../lib/Pagination.svelte';
    import SkeletonLoader from '../../lib/News/SkeletonLoader.svelte';
    import EmptyState from '../../lib/EmptyState.svelte';
    import { getBlogPosts } from '../../api/blogs.ts';
    import { getNewsletters } from '../../api/news.ts';
    import { vvSettings } from '../../api/common.ts';
    
    import news1 from '../../assets/news-placeholder-images/generated/news-placeholder-1_800.avif';
    import news2 from '../../assets/news-placeholder-images/generated/news-placeholder-2_800.avif';
    import news3 from '../../assets/news-placeholder-images/generated/news-placeholder-3_800.avif';
    import news4 from '../../assets/news-placeholder-images/generated/news-placeholder-4_800.avif';
    import news5 from '../../assets/news-placeholder-images/generated/news-placeholder-5_800.avif';
    import news6 from '../../assets/news-placeholder-images/generated/news-placeholder-6_800.avif';
    import news7 from '../../assets/news-placeholder-images/generated/news-placeholder-7_800.avif';
    import news8 from '../../assets/news-placeholder-images/generated/news-placeholder-8_800.avif';
    import news9 from '../../assets/news-placeholder-images/generated/news-placeholder-9_800.avif';
    import news10 from '../../assets/news-placeholder-images/generated/news-placeholder-10_800.avif';
    import news11 from '../../assets/news-placeholder-images/generated/news-placeholder-11_800.avif';
    import news12 from '../../assets/news-placeholder-images/generated/news-placeholder-12_800.avif';
    import news13 from '../../assets/news-placeholder-images/generated/news-placeholder-13_800.avif';
    import news14 from '../../assets/news-placeholder-images/generated/news-placeholder-14_800.avif';
    import news15 from '../../assets/news-placeholder-images/generated/news-placeholder-15_800.avif';
    import news16 from '../../assets/news-placeholder-images/generated/news-placeholder-16_800.avif';

    import blogs1 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-1_800.avif';
    import blogs2 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-2_800.avif';
    import blogs3 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-3_800.avif';
    import blogs5 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-5_800.avif';
    import blogs6 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-6_800.avif';
    import blogs7 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-7_800.avif';
    import blogs8 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-8_800.avif';
    import blogs9 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-9_800.avif';
    import blogs10 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-10_800.avif';
    import blogs11 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-11_800.avif';
    import blogs12 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-12_800.avif';
    import blogs13 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-13_800.avif';
    import blogs14 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-14_800.avif';
    import blogs15 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-15_800.avif';
    import blogs16 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-16_800.avif';
    import blogs17 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-17_800.avif';
    import blogs18 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-18_800.avif';
    import blogs19 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-19_800.avif';
    import blogs20 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-20_800.avif';
    import blogs21 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-21_800.avif';
    import blogs22 from '../../assets/blogs-placeholder-images/generated/blogs-placeholder-22_800.avif';

    const TABS = {
        BLOGS: 'blogs',
        NEWS: 'news'
    };
    const newsPlaceholders = [news1, news2, news3, news4, news5, news6, news7, news8, news9, news10, news11, news12, news13, news14, news15, news16];
    const blogPlaceholders = [
        blogs1, blogs2, blogs3, blogs5, blogs6, blogs7, blogs8, blogs9, blogs10,
        blogs11, blogs12, blogs13, blogs14, blogs15, blogs16, blogs17, blogs18, blogs19,
        blogs20, blogs21, blogs22
    ];
    
    const showBlogs = vvSettings?.showBlogs ?? true;
    const pageHeader = showBlogs ? t.Page_Header() : t.News_Tab();

    function getRandomImages(count: number, pool: string[]): string[] {
        const shuffled = [...pool].sort(() => Math.random() - 0.5);
        return shuffled.slice(0, count);
    }

    function assignBlogImages(posts: BlogPost[], imagePool: string[]): string[] {
        const shuffled = [...imagePool].sort(() => Math.random() - 0.5);
        return posts.map((post, index) => post.imageUrl ?? shuffled[index % shuffled.length]);
    }

    let shuffledBlogImages: string[] = [];

    $: visibleBlogPosts = blogPosts.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);
    $: currentBlogImages = shuffledBlogImages.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    $: currentNewsImages = getRandomImages(filteredNewsletters.length < itemsPerPage ? filteredNewsletters.length : itemsPerPage, newsPlaceholders);
    $: visibleNewsletters = filteredNewsletters.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage);

    let activeTab = TABS.BLOGS;
    let blogPosts: BlogPost[] = [];
    let newsletters: Newsletter[] = [];
    let groupedNewsletters: { [year: string]: Newsletter[] } = {};
    let selectedYear: string | null = null;
    let loading = false;
    let error = '';

    let currentPage = 1;
    const itemsPerPage = 10;

    function groupNewslettersByYear(newsletters: Newsletter[]) {
        return newsletters.reduce((acc, newsletter) => {
            const year = new Date(newsletter.date).getFullYear().toString();
            if (!acc[year]) {
                acc[year] = [];
            }
            acc[year].push(newsletter);
            return acc;
        }, {} as { [year: string]: Newsletter[] });
    }

    async function switchTab(tab: string) {
        activeTab = tab;
        sessionStorage.setItem('activeTab', tab);
        currentPage = 1;
        loading = true;
        error = '';

        try {
            if (tab === TABS.BLOGS) {
                blogPosts = await getBlogPosts();
                shuffledBlogImages = assignBlogImages(blogPosts, blogPlaceholders);
            } else if (tab === TABS.NEWS) {
                newsletters = await getNewsletters();
                groupedNewsletters = groupNewslettersByYear(newsletters);

                // Reset selected year to latest when switching back to "News"
                selectedYear = Object.keys(groupedNewsletters).sort().reverse()[0];
            }
        } catch (err) {
            console.error(`Failed to fetch ${tab}:`, err);
            error = `Failed to load ${tab}.`;
        } finally {
            loading = false;
        }
    }

    function updateSelectedYear(year: string) {
        selectedYear = year;
        sessionStorage.setItem('selectedYear', year);
    }

    function setPage(page: number) {
        currentPage = page;
        window.scrollTo({ top: 0, behavior: 'smooth' });
    }

    onMount(async () => {
        const savedTab = sessionStorage.getItem('activeTab');
        const savedYear = sessionStorage.getItem('selectedYear');

        loading = true;
        error = '';

        try {
            const [blogs, news] = await Promise.all([getBlogPosts(), getNewsletters()]);
            blogPosts = blogs;
            newsletters = news;

            groupedNewsletters = groupNewslettersByYear(newsletters);
            shuffledBlogImages = assignBlogImages(blogPosts, blogPlaceholders);

            const shouldDefaultToNews = blogPosts.length === 0 && newsletters.length > 0;

            // If blogging is disabled, always show NEWS tab
            if (!showBlogs) {
                activeTab = TABS.NEWS;
            } else {
                activeTab = savedTab && (savedTab === TABS.BLOGS || savedTab === TABS.NEWS)
                    ? savedTab
                    : shouldDefaultToNews
                        ? TABS.NEWS
                        : TABS.BLOGS;
            }

            sessionStorage.setItem('activeTab', activeTab);

            if (activeTab === TABS.NEWS) {
                selectedYear = savedYear && groupedNewsletters[savedYear]
                    ? savedYear
                    : Object.keys(groupedNewsletters).sort().reverse()[0];
            }
        } catch (err) {
            console.error('Failed to fetch blog or news data:', err);
            error = 'Failed to load content.';
        } finally {
            loading = false;
        }
    });

    $: filteredNewsletters = selectedYear ? groupedNewsletters[selectedYear] || [] : newsletters;
</script>

<div class="@container w-full m-auto max-w-[1000px] py-2 px-8 md:px-2 flex flex-col gap-8 interfont">
    <div class="flex flex-col @xl:flex-row gap-4">
        <div class="flex-1">
            <h1 class="text-4xl mt-1">{pageHeader}</h1>
        </div>
    </div>

    {#if showBlogs}
        <Tabs
                activeTab={activeTab}
                onSwitchTab={switchTab}
                labels={{
                blogs: t.Blogs_Tab(),
                news: t.News_Tab()
            }}
        />
    {/if}

    <div class="mt-6">
        {#if activeTab === 'news'}
            {#if loading}
                <SkeletonLoader type={activeTab} {itemsPerPage} />
            {:else if error}
                <p class="text-red-500" data-testid="error">{t.Error_Message()}</p>
            {:else if newsletters.length === 0}
                <EmptyState/>
            {:else}
                <!-- Archive Year Dropdown -->
                <div class="flex gap-4 items-center mb-6">
                    <label for="year-select" class="text-gray-700 font-medium">{t.Archive_Label()}</label>
                    <select
                            id="archive-year"
                            aria-label="Select a year to filter newsletters"
                            class="border border-gray-300 px-3 py-2 rounded-lg shadow-sm bg-white cursor-pointer focus:outline-none focus:ring-2 focus:ring-blue-500 hover:bg-gray-100 active:bg-gray-200 transition duration-200 ease-in-out"
                            bind:value={selectedYear}
                            on:change={() => updateSelectedYear(selectedYear)}
                    >
                        {#each Object.keys(groupedNewsletters).sort().reverse() as year}
                            <option value={year}>{year}</option>
                        {/each}
                    </select>
                </div>

                <ul class="flex flex-col gap-6 p-0 list-none" data-testid="news-list">
                    {#each visibleNewsletters as newsletter, index}
                        <li><NewsCard {newsletter} image={currentNewsImages[index]} /></li>
                    {/each}
                </ul>
                <Pagination totalItems={filteredNewsletters.length} currentPage={currentPage} itemsPerPage={itemsPerPage} onPageChange={setPage} />
            {/if}
        {:else if activeTab === 'blogs'}
            {#if loading}
                <SkeletonLoader type={activeTab} {itemsPerPage} />
            {:else if error}
                <p class="text-red-500" data-testid="error">{t.Error_Message()}</p>
            {:else if blogPosts.length === 0}
                <EmptyState/>
            {:else}
                <ul class="flex flex-col gap-6 p-0 list-none" data-testid="blog-list">
                    {#each visibleBlogPosts as post, index}
                        <li><BlogCard {post} image={currentBlogImages[index]} /></li>
                    {/each}
                </ul>
                <Pagination totalItems={blogPosts.length} currentPage={currentPage} itemsPerPage={itemsPerPage} onPageChange={setPage} />
            {/if}
        {/if}
    </div>
</div>